/**
 * VideoUploader - Componente de subida de videos GOATGYM
 * 
 * Features:
 * - Drag & drop o click para seleccionar
 * - Preview instantáneo del video
 * - Progress bar durante upload
 * - Validación de formato y tamaño
 * - Optimizado para videos verticales 9:16
 * - Integración con Cloudinary via signed upload
 */
import { useState, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { 
  Upload, 
  Video, 
  X, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  Play,
  Trash2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Config
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_TYPES = ["video/mp4", "video/quicktime", "video/webm", "video/x-m4v"];
const ALLOWED_EXTENSIONS = [".mp4", ".mov", ".webm", ".m4v"];

const VideoUploader = ({ 
  exerciseId,
  currentVideoUrl,
  currentThumbnail,
  onUploadComplete,
  onVideoDelete,
  getAuthHeaders,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(""); // "preparing", "uploading", "processing", "complete"
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  // Cleanup preview URL on unmount
  const cleanupPreview = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  // Validate file
  const validateFile = (file) => {
    if (!file) return "No se seleccionó archivo";
    
    // Check type
    const isValidType = ALLOWED_TYPES.includes(file.type) || 
      ALLOWED_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!isValidType) {
      return `Formato no válido. Usa: ${ALLOWED_EXTENSIONS.join(", ")}`;
    }
    
    // Check size
    if (file.size > MAX_FILE_SIZE) {
      return `El video es muy grande (máx ${MAX_FILE_SIZE / 1024 / 1024}MB)`;
    }
    
    return null;
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    cleanupPreview();
    setError(null);
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }
    
    setSelectedFile(file);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  // Drag handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (disabled || uploading) return;
    
    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Input change
  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files?.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Upload to Cloudinary
  const uploadVideo = async () => {
    if (!selectedFile || !exerciseId) return;
    
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      // Phase 1: Get signature from backend
      setUploadPhase("preparing");
      setUploadProgress(5);
      
      const sigResponse = await fetch(
        `${API_URL}/api/cloudinary/signature?resource_type=video&folder=goatgym/exercises`,
        { headers: getAuthHeaders() }
      );
      
      if (!sigResponse.ok) {
        throw new Error("Error obteniendo firma de upload");
      }
      
      const sigData = await sigResponse.json();
      setUploadProgress(10);
      
      // Debug logging
      console.log("Cloudinary signature data received:", {
        cloud_name: sigData.cloud_name,
        api_key: sigData.api_key,
        timestamp: sigData.timestamp,
        folder: sigData.folder,
        signature: sigData.signature
      });
      
      // Phase 2: Upload to Cloudinary
      setUploadPhase("uploading");
      
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("api_key", sigData.api_key);
      formData.append("timestamp", sigData.timestamp.toString());
      formData.append("signature", sigData.signature);
      formData.append("folder", sigData.folder);
      
      const uploadUrl = `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/video/upload`;
      console.log("Uploading to:", uploadUrl);
      console.log("FormData params:", {
        api_key: sigData.api_key,
        timestamp: sigData.timestamp,
        signature: sigData.signature,
        folder: sigData.folder,
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        file_type: selectedFile.type
      });
      
      // Upload with progress tracking
      const uploadResult = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            // Map upload progress to 10-80%
            const percent = Math.round((e.loaded / e.total) * 70) + 10;
            setUploadProgress(percent);
          }
        });
        
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              reject(new Error("Error parsing Cloudinary response"));
            }
          } else {
            // Parse Cloudinary error response for better debugging
            let errorMessage = `Upload failed: ${xhr.status}`;
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              if (errorResponse.error && errorResponse.error.message) {
                errorMessage = `Cloudinary error: ${errorResponse.error.message}`;
              }
              console.error("Cloudinary upload error:", errorResponse);
            } catch (e) {
              console.error("Cloudinary raw error:", xhr.responseText);
            }
            reject(new Error(errorMessage));
          }
        });
        
        xhr.addEventListener("error", () => reject(new Error("Network error")));
        xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")));
        
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/video/upload`);
        xhr.send(formData);
      });
      
      setUploadProgress(85);
      
      // Phase 3: Notify backend
      setUploadPhase("processing");
      
      const completeResponse = await fetch(`${API_URL}/api/cloudinary/video/complete`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          exercise_id: exerciseId,
          public_id: uploadResult.public_id,
          secure_url: uploadResult.secure_url,
          thumbnail_url: null, // Backend generates this
          duration: uploadResult.duration,
          width: uploadResult.width,
          height: uploadResult.height,
          format: uploadResult.format,
          bytes: uploadResult.bytes
        })
      });
      
      if (!completeResponse.ok) {
        throw new Error("Error guardando video en base de datos");
      }
      
      const completeData = await completeResponse.json();
      
      setUploadProgress(100);
      setUploadPhase("complete");
      
      toast.success("Video subido correctamente");
      
      // Notify parent
      if (onUploadComplete) {
        onUploadComplete({
          video_url: completeData.video_url,
          thumbnail_url: completeData.thumbnail_url,
          poster_url: completeData.poster_url
        });
      }
      
      // Reset state after short delay
      setTimeout(() => {
        cleanupPreview();
        setSelectedFile(null);
        setUploading(false);
        setUploadProgress(0);
        setUploadPhase("");
      }, 1500);
      
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Error al subir el video");
      toast.error(err.message || "Error al subir el video");
      setUploading(false);
      setUploadPhase("");
    }
  };

  // Delete current video
  const handleDelete = async () => {
    if (!currentVideoUrl || !onVideoDelete) return;
    
    if (!window.confirm("¿Eliminar este video?")) return;
    
    await onVideoDelete();
  };

  // Cancel selection
  const cancelSelection = () => {
    cleanupPreview();
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Get phase text
  const getPhaseText = () => {
    switch (uploadPhase) {
      case "preparing": return "Preparando...";
      case "uploading": return "Subiendo video...";
      case "processing": return "Procesando...";
      case "complete": return "¡Completado!";
      default: return "";
    }
  };

  // Render current video
  const renderCurrentVideo = () => {
    if (!currentVideoUrl) return null;
    
    return (
      <div className="space-y-3">
        <div className="relative aspect-[9/16] max-h-[300px] bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#2A2A2A]">
          <video
            src={currentVideoUrl}
            poster={currentThumbnail}
            controls
            className="w-full h-full object-contain"
            playsInline
          />
          {/* Delete button */}
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={handleDelete}
            className="absolute top-2 right-2 h-8 w-8 bg-red-500/80 hover:bg-red-500"
            disabled={disabled || uploading}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-green-400 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" />
          Video activo en Cloudinary CDN
        </p>
      </div>
    );
  };

  // Render preview
  const renderPreview = () => {
    if (!previewUrl) return null;
    
    return (
      <div className="space-y-3">
        <div className="relative aspect-[9/16] max-h-[300px] bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#D4AF37]/50">
          <video
            ref={videoRef}
            src={previewUrl}
            controls
            className="w-full h-full object-contain"
            playsInline
          />
          {!uploading && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={cancelSelection}
              className="absolute top-2 right-2 h-8 w-8 bg-[#0B0B0B]/80 hover:bg-[#0B0B0B]"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="truncate max-w-[200px]">{selectedFile?.name}</span>
          <span>{(selectedFile?.size / 1024 / 1024).toFixed(1)} MB</span>
        </div>
        
        {/* Upload progress */}
        {uploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} className="h-2" />
            <p className="text-xs text-[#D4AF37] flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              {getPhaseText()} {uploadProgress}%
            </p>
          </div>
        )}
        
        {/* Actions */}
        {!uploading && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={cancelSelection}
              className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={uploadVideo}
              className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
            >
              <Upload className="w-4 h-4 mr-2" />
              Subir Video
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render dropzone
  const renderDropzone = () => (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      className={cn(
        "border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer",
        isDragging 
          ? "border-[#D4AF37] bg-[#D4AF37]/10" 
          : "border-[#2A2A2A] hover:border-[#D4AF37]/50 bg-[#1A1A1A]",
        (disabled || uploading) && "opacity-50 cursor-not-allowed"
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || uploading}
      />
      
      <div className="space-y-3">
        <div className={cn(
          "w-12 h-12 rounded-full mx-auto flex items-center justify-center",
          isDragging ? "bg-[#D4AF37]/20" : "bg-[#2A2A2A]"
        )}>
          <Upload className={cn(
            "w-6 h-6",
            isDragging ? "text-[#D4AF37]" : "text-muted-foreground"
          )} />
        </div>
        
        <div>
          <p className="text-white font-medium">
            {isDragging ? "Suelta el video aquí" : "Arrastra un video o haz clic"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            MP4, MOV, WebM • Máx 100MB • Formato vertical 9:16 recomendado
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="video-uploader">
      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}
      
      {/* Current video (if exists and no new selection) */}
      {currentVideoUrl && !selectedFile && renderCurrentVideo()}
      
      {/* Preview of selected file */}
      {selectedFile && renderPreview()}
      
      {/* Dropzone (if no video and no selection) */}
      {!currentVideoUrl && !selectedFile && renderDropzone()}
      
      {/* Replace button (if has video) */}
      {currentVideoUrl && !selectedFile && !uploading && (
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="w-full border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
          disabled={disabled}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reemplazar Video
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v"
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled}
          />
        </Button>
      )}
    </div>
  );
};

export default VideoUploader;
